import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { HandIcon, MapPinIcon, PowerIcon, ReceiptTextIcon, StoreIcon } from 'lucide-react';

import { BillStatus, type LocationDTO } from '@invenco/common-interface/accounts';

import {
  AlertDialogWithAction,
  AlertDialogWithActionCancel,
  AlertDialogWithActionContent,
  AlertDialogWithActionDescription,
  AlertDialogWithActionFooter,
  AlertDialogWithActionHeader,
  AlertDialogWithActionSubmit,
  AlertDialogWithActionTitle,
  AlertDialogWithActionTrigger,
} from '@/components/alert-dialog-with-action';
import { Badge } from '@/components/core/badge';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  DataTableViewMenu,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
  type View,
} from '@/components/data-table';
import {
  DataTableAction,
  DataTableActionMenu,
} from '@/components/data-table/data-table-action-menu';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { DATA_POLLING_DURATION_MS } from '@/constants';
import { useGateways } from '@/gateways/GatewayProvider';
import { BillSortField, QueryBillsProps } from '@/gateways/impl/AccountsGateway';
import { formatDate, formatDateTime, formatMoney } from '@/shared/helpers';
import { Result } from '@/shared/helpers/Result';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { usePulseFlag } from '@/shared/hooks/use-pulse-flag';
import { useBatchedRequests } from '@/shared/hooks/useBatchedRequests';
import { useMessages } from '@/shared/providers/MessagesProvider';
import { billStatusType } from '@/shared/status-maps';
import { billStatusTitle } from '@/shared/title-maps';

import { BillHoldIndicator } from './bill-hold-indicator';
import { BillListItem, BillListMapper } from './bill-list-mapper';

export const POLL_INTERVAL = 1000;

const columnHelper = createColumnHelper<BillListItem>();

const columns = [
  columnHelper.accessor((row) => row.name, {
    id: 'billName',
    header: 'Bill',
    // The hold indicator leads the bill name (per the designs) and only shows
    // when the bill is held, so unheld rows read as a plain name.
    cell: ({ getValue, row }) => {
      const name = getValue();
      return (
        <span className="flex items-center gap-1.5">
          <BillHoldIndicator count={row.original.holds.length} />
          {name ? <Badge variant="secondary">{name}</Badge> : null}
        </span>
      );
    },
  }),
  columnHelper.accessor('accountName', { header: 'Account' }),
  columnHelper.accessor('locationName', { header: 'Location' }),
  columnHelper.accessor('periodStart', {
    header: 'From',
    cell: ({ getValue }) => formatDate(getValue()),
  }),
  columnHelper.accessor('periodEnd', {
    header: 'To',
    cell: ({ getValue }) => formatDate(getValue()),
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue();
      return status ? (
        <StatusBadge status={status} type={billStatusType} title={billStatusTitle} />
      ) : undefined;
    },
  }),
  columnHelper.accessor('recalculatedAt', {
    header: 'Last calculated',
    cell: ({ getValue }) => {
      const recalculatedAt = getValue();
      return recalculatedAt ? formatDateTime(recalculatedAt) : undefined;
    },
  }),
  columnHelper.accessor('billTotal', {
    header: 'Total',
    meta: { align: 'right' },
    cell: ({ getValue }) => formatMoney(getValue()),
  }),
];

const statusOrder = [BillStatus.OPEN, BillStatus.PENDING, BillStatus.CLOSED];

export const presetViews: View[] = [
  { id: 'all', name: 'All', state: { filters: [] } },
  ...statusOrder.map((status) => ({
    id: status,
    name: billStatusTitle[status],
    state: { filters: [{ id: 'status', operator: 'in', value: [status] }] },
  })),
  {
    id: 'held',
    name: 'Held',
    state: { filters: [{ id: 'hasHolds', operator: 'eq', value: true }] },
  },
];

const tieBreakerSort = [
  { key: 'createdAt', direction: 'desc' },
  { key: 'id', direction: 'asc' },
] as const;

// NOTE: Hasura does not carry allowable actions forcing us to replicate them in the FE
// Still safe since Lynk BE will fail when attempting the action if something went stale
// and the action is no longer allowed. But this is not an ideal UX
// A bill with one or more holds is blocked from closing until the holds clear.
const canCloseBill = (bill: BillListItem) =>
  bill.status === BillStatus.PENDING && bill.holds.length === 0;
const canRecalculateBill = (bill: BillListItem) =>
  bill.status === BillStatus.OPEN || bill.status === BillStatus.PENDING;

export function BillListShad() {
  const { accountsGateway } = useGateways();
  const messages = useMessages();
  const onClickRow = useRowNavigationCallback<BillListItem>('/bills-shad');
  const [isPolling, triggerPolling] = usePulseFlag(DATA_POLLING_DURATION_MS);

  const { run: closeBills, isRunning: isClosing } = useBatchedRequests<BillListItem, void>({
    messageKey: 'bill-close',
    progressMessage: ({ results, failures, total }) =>
      `Closed bills (${results.length}/${total}${failures.length ? ` - ${failures.length} failed` : ''})`,
  });

  const { run: recalculateBills, isRunning: isRecalculating } = useBatchedRequests<
    BillListItem,
    void
  >({
    messageKey: 'bill-recalculate',
    progressMessage: ({ results, failures, total }) =>
      `Recalculated bills (${results.length}/${total}${failures.length ? ` - ${failures.length} failed` : ''})`,
  });

  // Temporarily stub saved view CRUD methods so they show a 'not support' message
  // when platform user attempts to save a view
  // Saved views only work with SELLER context because they are tied to an account
  // Platform user is not tied to a specific account
  // Pending a product decision, we'll either disable saved views for PLATFORM or
  // build support and then clean up this stub. See ENG-5246
  const rejectSavedView = () => {
    messages.warning('Saving views is not available on this page yet');
    return Promise.resolve(Result.fail('Saved views are not supported here'));
  };

  const { data: locations, isLoading: isLoadingLocations } = useQuery({
    queryKey: ['locations'],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      accountsGateway.getLocations(undefined, { signal }),
    select: ({ items }: { items: LocationDTO[] }) => items,
  });

  const { state, currentView, onChangeViewId, filters, ...tableProps } = useDataTableState({
    columns,
    filterDefs: {
      accountName: {
        label: 'Account',
        variant: 'multiselect',
        icon: StoreIcon,
        options: async (input, options) => {
          const result = await accountsGateway.getAccounts(
            { search: input.search, take: input.take },
            options,
          );
          return { items: result.items.map((account) => account.name) };
        },
      },
      locationName: {
        label: 'Location',
        variant: 'multiselect',
        icon: MapPinIcon,
        // At platform level we see every account's locations, where a warehouse and the
        // managed locations pointing at it share a name. Bills are filtered by location
        // name (not id), so collapse to one option per name; the account filter narrows
        // further when a name spans multiple accounts.
        options: [...new Set(locations?.map((location) => location.name))],
        isLoading: isLoadingLocations,
      },
      status: {
        label: 'Status',
        variant: 'multiselect',
        icon: PowerIcon,
        options: statusOrder,
        optionLabel: (option) => billStatusTitle[option as BillStatus] || option,
      },
      hasHolds: {
        label: 'Hold',
        variant: 'boolean',
        icon: HandIcon,
        labels: {
          truthy: 'Yes',
          falsey: 'No',
        },
      },
    },
    views: presetViews,
  });

  const filterMap = filters.getActiveFilterMap();

  const queryInput: QueryBillsProps = {
    take: state.pagination.pageSize,
    offset: state.pagination.pageSize * state.pagination.pageIndex,
    sortBy: [
      ...state.sorting.map((sort) => ({
        key: sort.id as BillSortField,
        direction: sort.desc ? ('desc' as const) : ('asc' as const),
      })),
      ...tieBreakerSort,
    ],
    filters: {
      accountName: filterMap.accountName,
      locationName: filterMap.locationName,
      status: filterMap.status,
      hasHolds: filterMap.hasHolds,
    },
    search: filters.search,
  };

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'bills',
    input: queryInput,
    query: (input, { signal }) => accountsGateway.queryBills(input, { signal }),
    select: BillListMapper.toData,
    // The list reads from Hasura/ClickHouse via CDC, so poll briefly after a
    // mutation to let the change propagate, and keep the prior page meanwhile.
    refetchInterval: isPolling ? POLL_INTERVAL : false,
    placeholderData: keepPreviousData,
  });

  const table = useDataTable({
    data: data?.data,
    rowCount: data?.total,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    state,
    enableRowSelection: true,
    ...tableProps,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <ReceiptTextIcon />
        </HeaderIcon>
        <HeaderTitle>Bills</HeaderTitle>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableViewMenu
            views={presetViews}
            currentView={currentView}
            filterState={filters.filterState}
            rowCount={data?.total}
            onChangeViewId={onChangeViewId}
            onAddView={rejectSavedView}
            onUpdateView={rejectSavedView}
            onDeleteView={rejectSavedView}
          />
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
      <DataTableActionMenu table={table}>
        {(selectedRows) => (
          <>
            {selectedRows.length > 0 && selectedRows.every(canCloseBill) && (
              <AlertDialogWithAction
                onAction={async () => {
                  await closeBills({
                    execute: ({ id }) => accountsGateway.closeBill(id),
                    inputs: selectedRows,
                  });
                  triggerPolling();
                  table.resetRowSelection();
                  return Result.ok();
                }}
              >
                <AlertDialogWithActionTrigger asChild>
                  <DataTableAction disabled={isClosing}>Close</DataTableAction>
                </AlertDialogWithActionTrigger>
                <AlertDialogWithActionContent>
                  <AlertDialogWithActionHeader>
                    <AlertDialogWithActionTitle>
                      {`Close ${selectedRows.length} bill${selectedRows.length === 1 ? '' : 's'}?`}
                    </AlertDialogWithActionTitle>
                    <AlertDialogWithActionDescription>
                      A closed bill cannot be reopened. Continue only when all charges are final.
                    </AlertDialogWithActionDescription>
                  </AlertDialogWithActionHeader>
                  <AlertDialogWithActionFooter>
                    <AlertDialogWithActionCancel>Cancel</AlertDialogWithActionCancel>
                    <AlertDialogWithActionSubmit>Close</AlertDialogWithActionSubmit>
                  </AlertDialogWithActionFooter>
                </AlertDialogWithActionContent>
              </AlertDialogWithAction>
            )}
            {selectedRows.length > 0 && selectedRows.every(canRecalculateBill) && (
              <DataTableAction
                disabled={isRecalculating}
                onClick={async () => {
                  await recalculateBills({
                    execute: ({ id }) => accountsGateway.recalculateBill(id),
                    inputs: selectedRows,
                  });
                  triggerPolling();
                  table.resetRowSelection();
                }}
              >
                Recalculate
              </DataTableAction>
            )}
          </>
        )}
      </DataTableActionMenu>
    </Page>
  );
}
